import React, { useContext, useEffect, useLayoutEffect, useState } from 'react';
import { BrowserRouter as Router, Switch, Route } from 'react-router-dom';
import defaultTheme from 'styles/themes/default';
import Normalize from './styles/Normalize';
import GlobalStyles from './styles/GlobalStyle';
import AuthRoute from 'router/AuthRouter';
import * as PAGE_URL from 'router/PageUrl';
import { ThemeProvider } from 'styled-components';
import { PartnerAuthAction } from 'stores/PartnerAuthStore';
import { IPartnerAuthState } from 'stores/PartnerAuthStore/state';
import SystemCheck from 'pages/SystemCheck';

import { apiTokenCheck, apiGetMaintenance } from 'api/ZimssaAPI';
import {
  Main,
  PartnerSignUp,
  PartnerSurvey,
  PartnerLogin,
  ServiceSelect,
  SmallMoveServiceInfo,
  SmallMoveServiceRegister,
  SmallMoveCarInfoRegister,
  FamilyMoveServiceInfo,
  FamilyMoveServiceRegister,
  FamilyMoveCarInfoRegister,
  MoveCleaningServiceInfo,
  MoveCleaningServiceRegister,
  MoveCleaningProfileRegister,
  ServiceDocReview,
  ServiceCertInfo,
  ProfileRegister,
  PartnerCreated,
  ServiceApproveReject,
  ServiceDocChecked,
  ServiceDocReject,
  ServiceDocWait,
  ServiceInterviewNotice,
  ServiceInterviewAnswer,
  ServiceInterviewNoAnswer,
  ServiceInterviewNoApprove,
  ServiceInterviewApprove,
  UserApproveCheck,
  UserApproveReject,
  UserList,
  UserRegister,
  FeatureRegister,
  ServiceFeatureInfo,
  MyProfile,
  UserInfo,
  ErrorPageNotFound,
  AppDownLoad,
  AppGuide,
} from 'pages';

interface Maintenance {
  maintenance?: boolean;
  message?: string;
}

const App: React.FC = () => {
  const accessToken = localStorage.getItem('access-token');
  const dispatcher = useContext(PartnerAuthAction);
  const [maintenanceData, setMaintenanceData] = useState<Maintenance>({
    maintenance: false,
    message: '',
  });

  useLayoutEffect(() => {
    // maintenance 값 받아와서 현재 maintenance 중이면 systemCheck 페이지로 redirect
    apiGetMaintenance()
      .then((response) => {
        const { maintenance, message } = response;

        if (maintenance === true) {
          setMaintenanceData({ maintenance, message });
        }
      })
      .catch((error) => {
        console.error(error);
      });
  }, []);

  useEffect(() => {
    // 로컬 스토리지에 유저 정보가 있으면 API 요청하여 정보 갱신,
    // 서버 쿠키가 만료 되었으면 로그아웃 처리
    if (accessToken) {
      apiTokenCheck(accessToken)
        .then((response) => {
          const { result, data } = response;
          if (result === 'success' && data) {
            const responseData: IPartnerAuthState = data;
            dispatcher({
              type: 'LOGIN',
              value: {
                ...Object.assign(responseData, { isLogin: true }),
              },
            });
          } else {
            localStorage.removeItem('access-token');
            dispatcher({ type: 'LOGOUT' });
          }
        })
        .catch(() => {
          localStorage.removeItem('access-token');
          dispatcher({ type: 'LOGOUT' });
        });
    } else {
      localStorage.removeItem('access-token');
      dispatcher({ type: 'LOGOUT' });
    }
  }, []);

  const isSystemCheck = maintenanceData?.maintenance;
  if (isSystemCheck) {
    return <SystemCheck maintenanceData={maintenanceData} />;
  }

  return (
    <ThemeProvider theme={defaultTheme}>
      <Normalize />
      <Router>
        <Switch>
          <AuthRoute
            exact
            pathValid={PAGE_URL.MAIN}
            path={PAGE_URL.MAIN.path}
            component={Main}
          />
          <AuthRoute
            exact
            pathValid={PAGE_URL.SIGNUP}
            path={PAGE_URL.SIGNUP.path}
            component={PartnerSignUp}
          />
          <AuthRoute
            exact
            pathValid={PAGE_URL.SURVEY}
            path={PAGE_URL.SURVEY.path}
            component={PartnerSurvey}
          />
          <AuthRoute
            exact
            pathValid={PAGE_URL.LOGIN}
            path={PAGE_URL.LOGIN.path}
            component={PartnerLogin}
          />
          <AuthRoute
            exact
            pathValid={PAGE_URL.SERVICE_SELECT}
            path={PAGE_URL.SERVICE_SELECT.path}
            component={ServiceSelect}
          />
          <AuthRoute
            exact
            pathValid={PAGE_URL.SMALLMOVE_INFO}
            path={PAGE_URL.SMALLMOVE_INFO.path}
            component={SmallMoveServiceInfo}
          />
          <AuthRoute
            exact
            pathValid={PAGE_URL.SMALLMOVE_REGISTER}
            path={PAGE_URL.SMALLMOVE_REGISTER.path}
            component={SmallMoveServiceRegister}
          />
          <AuthRoute
            exact
            pathValid={PAGE_URL.SMALLMOVE_CARINFO_REGISTER}
            path={PAGE_URL.SMALLMOVE_CARINFO_REGISTER.path}
            component={SmallMoveCarInfoRegister}
          />
          <AuthRoute
            exact
            pathValid={PAGE_URL.FAMILYMOVE_INFO}
            path={PAGE_URL.FAMILYMOVE_INFO.path}
            component={FamilyMoveServiceInfo}
          />
          <AuthRoute
            exact
            pathValid={PAGE_URL.FAMILYMOVE_REGISTER}
            path={PAGE_URL.FAMILYMOVE_REGISTER.path}
            component={FamilyMoveServiceRegister}
          />
          <AuthRoute
            exact
            pathValid={PAGE_URL.FAMILYMOVE_CARINFO_REGISTER}
            path={PAGE_URL.FAMILYMOVE_CARINFO_REGISTER.path}
            component={FamilyMoveCarInfoRegister}
          />
          <AuthRoute
            exact
            pathValid={PAGE_URL.MOVECLEANING_INFO}
            path={PAGE_URL.MOVECLEANING_INFO.path}
            component={MoveCleaningServiceInfo}
          />
          <AuthRoute
            exact
            pathValid={PAGE_URL.MOVECLEANING_REGISTER}
            path={PAGE_URL.MOVECLEANING_REGISTER.path}
            component={MoveCleaningServiceRegister}
          />
          <AuthRoute
            exact
            pathValid={PAGE_URL.MOVECLEANING_PROFILE_REGISTER}
            path={PAGE_URL.MOVECLEANING_PROFILE_REGISTER.path}
            component={MoveCleaningProfileRegister}
          />
          <AuthRoute
            exact
            pathValid={PAGE_URL.DOC_REVIEW}
            path={PAGE_URL.DOC_REVIEW.path}
            component={ServiceDocReview}
          />
          <AuthRoute
            exact
            pathValid={PAGE_URL.CERT_INFO}
            path={PAGE_URL.CERT_INFO.path}
            component={ServiceCertInfo}
          />
          <AuthRoute
            exact
            pathValid={PAGE_URL.PROFILE_REGISTER}
            path={PAGE_URL.PROFILE_REGISTER.path}
            component={ProfileRegister}
          />
          <AuthRoute
            exact
            pathValid={PAGE_URL.PARTNER_COMPLETED}
            path={PAGE_URL.PARTNER_COMPLETED.path}
            component={PartnerCreated}
          />
          <AuthRoute
            exact
            pathValid={PAGE_URL.SERVICE_APPROVEREJECT}
            path={PAGE_URL.SERVICE_APPROVEREJECT.path}
            component={ServiceApproveReject}
          />
          <AuthRoute
            exact
            pathValid={PAGE_URL.SERVICE_INTERVIEWAPPROVE}
            path={PAGE_URL.SERVICE_INTERVIEWAPPROVE.path}
            component={ServiceInterviewApprove}
          />
          <AuthRoute
            exact
            pathValid={PAGE_URL.SERVICE_DOCCHECKED}
            path={PAGE_URL.SERVICE_DOCCHECKED.path}
            component={ServiceDocChecked}
          />
          <AuthRoute
            exact
            pathValid={PAGE_URL.SERVICE_DOCREJECT}
            path={PAGE_URL.SERVICE_DOCREJECT.path}
            component={ServiceDocReject}
          />
          <AuthRoute
            exact
            pathValid={PAGE_URL.SERVICE_DOCWAIT}
            path={PAGE_URL.SERVICE_DOCWAIT.path}
            component={ServiceDocWait}
          />
          <AuthRoute
            exact
            pathValid={PAGE_URL.SERVICE_INTERVIEWANSWER}
            path={PAGE_URL.SERVICE_INTERVIEWANSWER.path}
            component={ServiceInterviewAnswer}
          />
          <AuthRoute
            exact
            pathValid={PAGE_URL.SERVICE_INTERVIEWNOANSWER}
            path={PAGE_URL.SERVICE_INTERVIEWNOANSWER.path}
            component={ServiceInterviewNoAnswer}
          />
          <AuthRoute
            exact
            pathValid={PAGE_URL.SERVICE_INTERVIEWNOAPPROVE}
            path={PAGE_URL.SERVICE_INTERVIEWNOAPPROVE.path}
            component={ServiceInterviewNoApprove}
          />
          <AuthRoute
            exact
            pathValid={PAGE_URL.SERVICE_INTERVIEWNOTICE}
            path={PAGE_URL.SERVICE_INTERVIEWNOTICE.path}
            component={ServiceInterviewNotice}
          />
          <AuthRoute
            exact
            pathValid={PAGE_URL.MY_PROFILE}
            path={PAGE_URL.MY_PROFILE.path}
            component={MyProfile}
          />
          <AuthRoute
            exact
            pathValid={PAGE_URL.USER_INFO}
            path={PAGE_URL.USER_INFO.path}
            component={UserInfo}
          />
          <AuthRoute
            exact
            pathValid={PAGE_URL.USER_APPROVE_CHECK}
            path={PAGE_URL.USER_APPROVE_CHECK.path}
            component={UserApproveCheck}
          />
          <AuthRoute
            exact
            pathValid={PAGE_URL.USER_APPROVE_REJECT}
            path={PAGE_URL.USER_APPROVE_REJECT.path}
            component={UserApproveReject}
          />
          <AuthRoute
            exact
            pathValid={PAGE_URL.SERVICE_FEATURE_REGISTER}
            path={PAGE_URL.SERVICE_FEATURE_REGISTER.path}
            component={FeatureRegister}
          />
          <AuthRoute
            exact
            pathValid={PAGE_URL.SERVICE_FEATURE}
            path={PAGE_URL.SERVICE_FEATURE.path}
            component={ServiceFeatureInfo}
          />
          <AuthRoute
            exact
            pathValid={PAGE_URL.USER_LIST}
            path={PAGE_URL.USER_LIST.path}
            component={UserList}
          />
          <AuthRoute
            exact
            pathValid={PAGE_URL.USER_REGISTER}
            path={PAGE_URL.USER_REGISTER.path}
            component={UserRegister}
          />
          <Route exact path={PAGE_URL.APP_GUIDE.path} component={AppGuide} />
          <Route exact path={'/app-download'} component={AppDownLoad} />
          <Route component={ErrorPageNotFound} />
        </Switch>
      </Router>
      <GlobalStyles />
    </ThemeProvider>
  );
};

export default App;
